import { ReportChartMeasureType, ReportQueryType } from '@vision/ui/interfaces';

/*
old clojure code

(def measure->query_type
  {:feedback/count              :count
   :feedback/sentiment-score    :sentiment_score,
   :feedback/csat               :positivity
   :feedback/weighted-score     :custom_score
   :feedback/power-score        :power_score
   :feedback/chat-score         :rating
   :feedback/scoreboard         :leaderboard
   :feedback/positivity-matrix  :positivity-matrix
   :feedback/multiple-matrix-question-csat-overview :multiple-matrix-question-csat-overview
   :feedback/elc-score          :elc_score
   :feedback/candidate-score    :candidate_score
   :feedback/maxdiff-score      :maxdiff_score
   :feedback/nps                :score

   :overview/general      :overview
   :overview/compared     :comparison
   :overview/distribution :distribution
   :overview/ranking      :ranking

   :action-time/completion    :time_spent_in_seconds
   :action-time/read          :action_metric
   :action-time/response      :action_metric
   :action-time/internal-note :action_metric
   :action-time/archived      :action_metric

   :response/count :count

   :response/matrix :matrix

   :customer/count :count

   :funnel/funnel :funnel

   :cati/metrics :cati_metrics

   :cx/metrics :cx_metrics

   :kda/count :count

   :funnel/metrics :funnel_campaign})
*/

// measure type'ı query type'a çevirirken kullanılacak mapping.
export const ReportGraphicQueryTypeMapping: Partial<Record<ReportChartMeasureType, ReportQueryType>> = {
  'feedback-count': 'count',
  'feedback-nps': 'score',
  'feedback-csat': 'positivity',
  'feedback-ces': 'ces',
  'feedback-sentiment-score': 'sentiment_score',
  'feedback-weighted-score': 'custom_score',
  'feedback-chat-score': 'rating',
  'feedback-elc-score': 'elc_score',
  'feedback-candidate-score': 'custom_score',
  'feedback-scoreboard': 'leaderboard',
  'action-time-completion': 'time_spent_in_seconds',
  'action-time-read': 'action_metric',
  'action-time-response': 'action_metric',
  'action-time-internal-note': 'action_metric',
  'action-time-archived': 'action_metric',
  'response-count': 'count',
  'response-ranking-overview': 'count',
  'matrix-question-csat-overview': 'multiple-matrix-question-csat-overview',
  'funnel-metrics': 'funnel_campaign',
  'csat-kpi': 'csat_kpi',
  'nps-kpi': 'nps_kpi',
  'cati-metrics': 'cati_metrics',
  'cx-metrics': 'cx_metrics',
  'customer-count': 'count',
  'matrix-question-response-count': 'matrix',
  'overview-compared': 'comparison',
  'overview-distribution': 'distribution',
  'nps-realization-rate': 'nps_realization_rate',
  'csat-realization-rate': 'csat_realization_rate',
  'star-rating': 'star_rating',
  'ticket-count': 'count',
  'ticket-reopen-count': 'reopen_count',
  'ticket-reopen-rate': 'reopen_rate',
  'ticket-average-time-to-first-resolution': 'average_time_to_first_resolution',
  'ticket-average-time-in-status': 'average_time_in_status',
  'ticket-average-time-to-latest-resolution': 'average_time_to_latest_resolution',
};

// query type'ı measure type'a çevirirken kullanılacak mapping.
export function getReportChartMeasureType(queryType: ReportQueryType): ReportChartMeasureType {
  if (!queryType) {
    return null;
  }

  const measureType = Object.entries(ReportGraphicQueryTypeMapping).find(([, value]) => value === queryType);
  if (!measureType) {
    throw new Error('Invalid query type');
  }
  return measureType[0] as ReportChartMeasureType;
}

// buradaki type lar interface altına taşınacak
export type ReportChartMeasureApiPathType =
  | 'feedback/count'
  | 'feedback/fcr'
  | 'feedback/score'
  | 'feedback/sentiment_score'
  | 'feedback/positivity'
  | 'feedback/ces'
  | 'feedback/custom_score'
  | 'feedback/power_score'
  | 'feedback/rating'
  | 'feedback/leaderboard'
  | 'feedback/positivity_matrix'
  | 'feedback/overview'
  | 'feedback/ranking'
  | 'feedback/comparison'
  | 'feedback/distribution'
  | 'feedback/time_spent_in_seconds'
  | 'feedback/metric'
  | 'responses/matrix'
  | 'feedback/cx_metrics'
  | 'campaigns/funnel'
  | 'feedback/nps_kpi'
  | 'feedback/csat_kpi'
  | 'feedback/nps_realization_rate'
  | 'feedback/csat_realization_rate'
  | 'feedback/star_rating'
  | 'cati/metrics'
  | 'tickets/count'
  | 'tickets/reopen_count'
  | 'tickets/reopen_rate'
  | 'tickets/average_time_to_first_resolution'
  | 'tickets/average_time_in_status'
  | 'tickets/average_time_to_latest_resolution';

/*


(def measure->api-path
  {:feedback/count              "/feedback/count"
   :feedback/nps                "/feedback/score"
   :feedback/sentiment-score    "/feedback/sentiment_score"
   :feedback/csat               "/feedback/positivity"
   :feedback/weighted-score     "/feedback/custom_score"
   :feedback/power-score        "/feedback/power_score"
   :feedback/chat-score         "/feedback/rating"
   :feedback/scoreboard         "/feedback/leaderboard"
   :feedback/positivity-matrix  "/feedback/positivity_matrix"
   :feedback/multiple-matrix-question-csat-overview "/feedback/positivity_matrix"
   :feedback/elc-score          "/feedback/custom_score"
   :feedback/candidate-score    "/feedback/custom_score"
   :overview/general            "/feedback/overview"
   :overview/ranking            "/feedback/ranking"
   :overview/compared           "/feedback/comparison"
   :overview/distribution       "/feedback/distribution"
   :action-time/completion      "/feedback/time_spent_in_seconds"
   :action-time/read            "/feedback/metric"
   :action-time/response        "/feedback/metric"
   :action-time/internal-note   "/feedback/metric"
   :action-time/archived        "/feedback/metric"
   :response/count              "/feedback/count"
   :response/matrix             "/responses/matrix"
   :customer/count              "/feedback/count"
   :cati/metrics                "/cati/metrics"
   :cx/metrics                  "/feedback/cx_metrics"
   :kda/count                   "/feedback/count"
   :funnel/metrics              "/campaigns/funnel"})
*/
export function getReportChartMeasureApiPath(measure: ReportChartMeasureType): ReportChartMeasureApiPathType {
  switch (measure) {
    case 'feedback-fcr':
      return 'feedback/fcr';
    case 'feedback-nps':
      return 'feedback/score';
    case 'feedback-csat':
      return 'feedback/positivity';
    case 'feedback-ces':
      return 'feedback/ces';
    case 'feedback-sentiment-score':
      return 'feedback/sentiment_score';
    case 'feedback-weighted-score':
    case 'feedback-elc-score':
    case 'feedback-candidate-score':
      return 'feedback/custom_score';
    case 'feedback-chat-score':
      return 'feedback/rating';
    case 'feedback-scoreboard':
      return 'feedback/leaderboard';
    case 'action-time-completion':
      return 'feedback/time_spent_in_seconds';
    case 'action-time-read':
    case 'action-time-response':
    case 'action-time-internal-note':
    case 'action-time-archived':
      return 'feedback/metric';
    case 'matrix-question-response-count':
      return 'responses/matrix';
    case 'matrix-question-csat-overview':
      return 'feedback/positivity_matrix';
    case 'funnel-metrics':
      return 'campaigns/funnel';
    case 'nps-kpi':
      return 'feedback/nps_kpi';
    case 'csat-kpi':
      return 'feedback/csat_kpi';
    case 'cati-metrics':
      return 'cati/metrics';
    case 'cx-metrics':
      return 'feedback/cx_metrics';
    case 'overview-compared':
      return 'feedback/comparison';
    case 'overview-distribution':
      return 'feedback/distribution';
    case 'feedback-count':
    case 'customer-count':
    case 'response-count':
    case 'response-ranking-overview':
      return 'feedback/count';
    case 'nps-realization-rate':
      return 'feedback/nps_realization_rate';
    case 'csat-realization-rate':
      return 'feedback/csat_realization_rate';
    case 'star-rating':
      return 'feedback/star_rating';
    case 'ticket-count':
      return 'tickets/count';
    case 'ticket-reopen-count':
      return 'tickets/reopen_count';
    case 'ticket-reopen-rate':
      return 'tickets/reopen_rate';
    case 'ticket-average-time-to-first-resolution':
      return 'tickets/average_time_to_first_resolution';
    case 'ticket-average-time-in-status':
      return 'tickets/average_time_in_status';
    case 'ticket-average-time-to-latest-resolution':
      return 'tickets/average_time_to_latest_resolution';
    default:
      return 'feedback/count';
  }
}
